/**
 * Canonical plan-build stages. Only these four are used by the UI.
 */
export const PlanBuildStage = {
  ANALYZE_RECENT_TRAINING: 'ANALYZE_RECENT_TRAINING',
  ANALYZE_TRAINING_LOAD: 'ANALYZE_TRAINING_LOAD',
  ANALYZE_GOALS_AND_PLAN: 'ANALYZE_GOALS_AND_PLAN',
  GENERATE_TRAINING_PLAN: 'GENERATE_TRAINING_PLAN',
} as const;

export type PlanBuildStageValue = (typeof PlanBuildStage)[keyof typeof PlanBuildStage];

const STAGE_ORDER: PlanBuildStageValue[] = [
  PlanBuildStage.ANALYZE_RECENT_TRAINING,
  PlanBuildStage.ANALYZE_TRAINING_LOAD,
  PlanBuildStage.ANALYZE_GOALS_AND_PLAN,
  PlanBuildStage.GENERATE_TRAINING_PLAN,
];

export interface PlanBuildProgressMetadata {
  week?: number;
  total_weeks?: number;
  recent_activities_analyzed?: number;
  ctl?: number;
  atl?: number;
  tsb?: number;
}

/**
 * Progress payload from backend (ConversationProgress.outbound_progress).
 * API returns snake_case; we use as-is.
 */
export interface PlanBuildProgress {
  status: 'IN_PROGRESS' | 'DONE';
  stage: PlanBuildStageValue;
  started_at: string;
  completed_at?: string | null;
  metadata?: PlanBuildProgressMetadata | null;
}

export type PlanProgressStageState = 'PENDING' | 'IN_PROGRESS' | 'DONE';

export interface PlanProgressStageDisplay {
  description: string;
  state: PlanProgressStageState;
}

const STAGE_LABELS: Record<PlanBuildStageValue, string> = {
  [PlanBuildStage.ANALYZE_RECENT_TRAINING]: 'Analyzing your recent training',
  [PlanBuildStage.ANALYZE_TRAINING_LOAD]: 'Analyzing your training load status',
  [PlanBuildStage.ANALYZE_GOALS_AND_PLAN]: 'Analyzing your goals and existing plan',
  [PlanBuildStage.GENERATE_TRAINING_PLAN]: 'Generating your training plan',
};

function hasCompleted(stageKey: PlanBuildStageValue, currentStage: PlanBuildStageValue): boolean {
  const currentIndex = STAGE_ORDER.indexOf(currentStage);
  const stageIndex = STAGE_ORDER.indexOf(stageKey);
  return stageIndex < currentIndex;
}

/**
 * Build plan progress content for UI from backend outboundProgress.
 * No timers; 1:1 with backend signals.
 */
export function buildPlanProgressContent(
  outboundProgress: PlanBuildProgress | null | undefined
): {
  initialMessage: string;
  /** e.g. "Generating week 7 of 16" when stage is GENERATE_TRAINING_PLAN and metadata has week/total_weeks */
  subMessage: string | null;
  stages: PlanProgressStageDisplay[];
} {
  const stages: PlanProgressStageDisplay[] = STAGE_ORDER.map((key) => ({
    description: STAGE_LABELS[key],
    state: !outboundProgress
      ? ('PENDING' as const)
      : key === outboundProgress.stage
        ? ('IN_PROGRESS' as const)
        : hasCompleted(key, outboundProgress.stage)
          ? ('DONE' as const)
          : ('PENDING' as const),
  }));

  const meta = outboundProgress?.metadata;
  const subMessage =
    outboundProgress?.stage === PlanBuildStage.GENERATE_TRAINING_PLAN &&
    meta?.week != null &&
    meta?.total_weeks != null
      ? `Generating week ${meta.week} of ${meta.total_weeks}`
      : null;

  return {
    initialMessage: 'Coach is creating your plan…',
    subMessage,
    stages,
  };
}
